/**
 * pwa.js — detecção de instalação do PWA e assinatura de Web Push.
 *
 * Contexto do produto: notificação push só funciona no iOS quando o site foi
 * adicionado à Tela de Início (regra do Safari, não nossa). Como boa parte
 * dos alunos usa iPhone, o app precisa detectar quem está só no navegador e
 * pedir a instalação — ver InstallPwaModal.jsx e o badge no header do
 * Dashboard.
 *
 * Nenhuma função aqui lança exceção: são todas "best effort". Notificação é
 * um extra, nunca pode quebrar a tela do aluno.
 */
import api from "../services/api";

/**
 * O app está rodando instalado (standalone) ou dentro de uma aba do navegador?
 *
 * Duas checagens porque nenhuma cobre todos os alvos:
 *  - `display-mode: standalone` é o padrão (Android/Chrome, desktop)
 *  - `navigator.standalone` é a versão legada e proprietária do Safari iOS,
 *    que até hoje não reporta o display-mode corretamente em todos os casos
 */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    const byMedia =
      typeof window.matchMedia === "function" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches);
    const byIosLegacy = window.navigator.standalone === true;
    return !!(byMedia || byIosLegacy);
  } catch (_) {
    return false;
  }
}

/**
 * Sistema operacional do dispositivo: "ios" | "android" | "other".
 *
 * O teste de iPad usa `maxTouchPoints` porque o iPadOS moderno se identifica
 * como "Macintosh" no user agent — sem isso, iPad cairia em "other" e
 * receberia o passo a passo errado.
 */
export function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";

  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios"; // iPadOS
  return "other";
}

// R366: navegador embutido de rede social/mensageria (WebView que abre por
// dentro do próprio app, não o navegador do aparelho). O UA desses WebViews
// no iOS costuma incluir "Version/... Safari/..." igual ao Safari de
// verdade — sem checar essas marcas ANTES, detectBrowser() classificava tudo
// isso como "safari" e mostrava o passo a passo normal ("toque em
// Compartilhar"), que não bate com a tela real (o compartilhar ali é do
// Instagram/Facebook/etc., sem a opção "Adicionar à Tela de Início" —
// trava o aluno sem nenhum erro aparecer). Cada entrada aqui é [chave,
// regex, rótulo pro aluno] — a chave também é o valor salvo em
// pwa_install_events.browser (ver app/models/pwa_install_event.py), pra dar
// pra medir quanto do funil vem de cada app.
//
// WhatsApp ficou de fora de propósito: no iOS ele abre link via
// SFSafariViewController (o próprio Safari, só emoldurado) e no Android via
// Chrome Custom Tabs — os dois preservam a opção de instalar, então não é o
// mesmo problema.
const IN_APP_BROWSER_PATTERNS = [
  ["instagram", /Instagram/i, "Instagram"],
  ["facebook", /FBAN|FBAV|FB_IAB|FBIOS/i, "Facebook"],
  ["tiktok", /musical_ly|BytedanceWebview|TikTok/i, "TikTok"],
  ["linkedin", /LinkedInApp/i, "LinkedIn"],
  ["twitter", /Twitter/i, "X (Twitter)"],
  ["snapchat", /Snapchat/i, "Snapchat"],
  ["line", /\bLine\//, "Line"],
];

/** Rótulo em português pra cada chave de IN_APP_BROWSER_PATTERNS — usado pelo InstallPwaModal. */
export const IN_APP_BROWSER_LABELS = Object.fromEntries(
  IN_APP_BROWSER_PATTERNS.map(([key, , label]) => [key, label])
);

/** Chave do app-container atual ("instagram", "facebook", ...) ou null se for um navegador de verdade. */
export function detectInAppBrowser() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  const hit = IN_APP_BROWSER_PATTERNS.find(([, re]) => re.test(ua));
  return hit ? hit[0] : null;
}

/** Navegador em uso, só o suficiente pra ajustar o passo a passo. */
export function detectBrowser() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  // Checagem de app embutido primeiro — tem prioridade sobre a detecção de
  // browser "de verdade" abaixo, exatamente pelo motivo explicado acima.
  const inApp = detectInAppBrowser();
  if (inApp) return inApp;
  // No iOS todo navegador roda em cima do WebKit, mas só o Safari "de
  // verdade" oferece "Adicionar à Tela de Início" — Chrome/Firefox no iPhone
  // não têm essa opção, então vale avisar o aluno.
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return "ios-other";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/Firefox/i.test(ua)) return "firefox";
  if (/Edg/i.test(ua)) return "edge";
  if (/Chrome/i.test(ua)) return "chrome";
  if (/Safari/i.test(ua)) return "safari";
  return "other";
}

/** O navegador consegue receber push AGORA, do jeito que está aberto. */
export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Instalar o app destravaria notificação neste aparelho?
 *
 * Cuidado — NÃO dá pra usar pushSupported() pra decidir se mostra o aviso de
 * instalação: no iOS, `PushManager` e `Notification` só passam a existir
 * DEPOIS que o site é adicionado à Tela de Início. Numa aba normal do Safari
 * eles são `undefined`, então pushSupported() é falso exatamente pra quem
 * mais precisa ver o convite — o aviso nunca apareceria no iPhone.
 *
 * Por isso o iOS é avaliado só pelo service worker (que existe na aba
 * normal); nos demais sistemas o teste completo vale, porque lá as APIs de
 * push já estão disponíveis no navegador comum.
 */
export function installUnlocksPush() {
  if (typeof window === "undefined") return false;
  if (detectPlatform() === "ios") return "serviceWorker" in navigator;
  return pushSupported();
}

/**
 * R370: telemetria best-effort do funil de instalação (POST /pwa/events —
 * ver app/api/pwa_telemetry.py). Existe pra responder, com dado real, a
 * pergunta que motivou toda a detecção de app embutido acima: quanto da
 * baixa adesão ao PWA vem de navegador embutido vs. outra causa.
 *
 * Nunca lança, nunca bloqueia — mesma regra do resto deste arquivo. `platform`
 * e `browser` são opcionais porque quem já tem os valores de usePwaInstall()
 * deve passá-los (evita rodar a detecção duas vezes); quando omitidos, a
 * própria função detecta.
 */
export function logPwaEvent(eventType, { platform, browser } = {}) {
  try {
    // Sem sessão (página-ponte /abrir aberta pelo email no Safari, por
    // exemplo), a rota autenticada devolvia 401 e o cliente da API tentava
    // renovar a sessão e mandava pro login no meio do passo a passo de
    // instalação. A rota pública aceita só os eventos do funil.
    let hasSession = false;
    try { hasSession = !!localStorage.getItem("token"); } catch (_) { /* sem storage */ }
    api.post(hasSession ? "/pwa/events" : "/pwa/events/public", {
      event_type: eventType,
      platform: platform || detectPlatform(),
      browser: browser || detectBrowser(),
    }).catch(() => {});
  } catch (_) { /* ambiente sem api/fetch — ignora */ }
}

/** Registra o service worker (idempotente — o browser deduplica). */
export async function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (err) {
    console.warn("[pwa] falha ao registrar o service worker:", err);
    return null;
  }
}

/** Converte a chave VAPID (base64url) no Uint8Array que o pushManager exige. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

let publicKeyPromise = null;

/**
 * Busca a chave pública do push uma vez e guarda a promessa. Existe por causa
 * do WebKit (Safari/iOS): o pedido de permissão precisa sair do toque do
 * usuário, e esperar uma chamada de rede ANTES de `requestPermission` pode
 * estourar a janela do gesto numa conexão lenta. Chamando isto quando o
 * convite aparece, no toque a chave já está em memória e o `await` resolve
 * na hora.
 */
export function prefetchPushPublicKey() {
  if (!publicKeyPromise) {
    publicKeyPromise = api.get("/push/public-key")
      .then((res) => res.data)
      .catch((err) => { publicKeyPromise = null; throw err; });
  }
  return publicKeyPromise;
}

/**
 * Fluxo completo: pede permissão, assina no push service do navegador e
 * registra a inscrição no backend.
 *
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function subscribeToPush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };

  // iOS: sem estar instalado, `Notification.requestPermission` até existe,
  // mas a inscrição falha. Melhor não queimar o pedido de permissão à toa.
  if (detectPlatform() === "ios" && !isStandalone()) {
    return { ok: false, reason: "ios-needs-install" };
  }

  try {
    const data = await prefetchPushPublicKey();
    if (!data?.enabled || !data?.public_key) return { ok: false, reason: "server-disabled" };

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: "denied" };

    const registration = (await navigator.serviceWorker.getRegistration("/")) || (await registerServiceWorker());
    if (!registration) return { ok: false, reason: "no-sw" };
    await navigator.serviceWorker.ready;

    // Reaproveita a inscrição existente quando houver — assinar de novo sem
    // necessidade invalida a anterior e deixa lixo no banco.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }

    const json = subscription.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });

    return { ok: true };
  } catch (err) {
    console.warn("[pwa] falha ao assinar push:", err);
    return { ok: false, reason: "error" };
  }
}

/** Cancela a inscrição no navegador e remove do backend. */
export async function unsubscribeFromPush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = registration && (await registration.pushManager.getSubscription());
    if (!subscription) return { ok: true };

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await api.post("/push/unsubscribe", { endpoint });
    return { ok: true };
  } catch (err) {
    console.warn("[pwa] falha ao cancelar push:", err);
    return { ok: false, reason: "error" };
  }
}
